import { File, Paths } from "expo-file-system";
import { Platform } from "react-native";

/**
 * Maximum file size (in bytes) that can safely be converted to an in-memory base64 string on mobile devices.
 * Base64 encoding inflates payload memory by ~33% and creates concurrent native Java and JavaScript string copies.
 * On Android devices with limited JVM heap growth limits (~192MB - 256MB), reading files > 30MB into base64 causes OutOfMemoryError.
 */
export const MAX_SAFE_BASE64_FILE_SIZE_BYTES = 30 * 1024 * 1024; // 30 MB

/**
 * Converts a Blob or File instance into a base64 string (without the data URL prefix).
 * Supports browser environments (FileReader), Node/Jest environments (Buffer/arrayBuffer), and btoa fallback.
 *
 * @param {Blob} blob - The Blob or File object to convert.
 * @returns {Promise<string>} Base64-encoded string.
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  if (typeof FileReader !== "undefined") {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        if (!result) {
          reject(new Error("Empty result when reading Blob"));
          return;
        }
        const base64 = result.includes(",") ? result.split(",")[1] : result;
        resolve(base64);
      };
      reader.onerror = (err) => reject(new Error(`FileReader failed: ${err}`));
      reader.readAsDataURL(blob);
    });
  }

  if (typeof (blob as any).arrayBuffer === "function") {
    const arrayBuffer = await blob.arrayBuffer();
    if (typeof Buffer !== "undefined") {
      return Buffer.from(arrayBuffer).toString("base64");
    }
    const bytes = new Uint8Array(arrayBuffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    if (typeof btoa !== "undefined") {
      return btoa(binary);
    }
  }

  throw new Error("No supported Blob reading mechanism available in current environment");
}

/**
 * Reads any URI (blob:, data:, file://, content://, local path, web URL) and converts it to a base64 string.
 * Supports web blob URLs, data URIs, Android content URIs, file URIs, and fallback mechanisms using next-gen Expo File API.
 * Proactively checks file size and handles OutOfMemoryError gracefully.
 *
 * @param {string} uri - The file, blob, or content URI to read.
 * @returns {Promise<string>} Base64-encoded file contents.
 */
export async function readFileAsBase64(uri: string): Promise<string> {
  if (!uri || typeof uri !== "string") {
    throw new Error("Invalid or empty URI passed to readFileAsBase64");
  }

  let targetUri = uri;

  // 1. Data URI: Extract base64 payload directly or parse data URL
  if (targetUri.startsWith("data:")) {
    const commaIndex = targetUri.indexOf(",");
    if (commaIndex !== -1) {
      const meta = targetUri.slice(0, commaIndex);
      if (meta.includes(";base64")) {
        return targetUri.slice(commaIndex + 1);
      }
      const rawData = decodeURIComponent(targetUri.slice(commaIndex + 1));
      if (typeof Buffer !== "undefined") {
        return Buffer.from(rawData, "utf-8").toString("base64");
      }
      if (typeof btoa !== "undefined") {
        return btoa(rawData);
      }
    }
  }

  // 2. Web Blob URIs and Network/Local Web URIs (e.g. blob:https://... or http(s):// on web)
  if (
    targetUri.startsWith("blob:") ||
    (Platform.OS === "web" && (targetUri.startsWith("http://") || targetUri.startsWith("https://") || targetUri.startsWith("/")))
  ) {
    try {
      const response = await fetch(targetUri);
      if (!response.ok && !targetUri.startsWith("blob:")) {
        throw new Error(`HTTP ${response.status} fetching URI: ${targetUri}`);
      }
      const blob = await response.blob();
      if (typeof blob.size === "number" && blob.size > MAX_SAFE_BASE64_FILE_SIZE_BYTES) {
        const sizeMb = (blob.size / (1024 * 1024)).toFixed(1);
        throw new Error(
          `File is too large for in-memory processing (${sizeMb} MB). Files larger than 30 MB exceed device memory limits.`
        );
      }
      const base64 = await blobToBase64(blob);
      if (base64) {
        return base64;
      }
    } catch (err: any) {
      if (err?.message?.includes("File is too large")) {
        throw err;
      }
      console.warn(`[FileReader] fetch/blob conversion failed for "${targetUri}":`, err?.message || err);
    }
  }

  // Normalize absolute path to file:// on native mobile devices
  if (Platform.OS !== "web" && targetUri.startsWith("/") && !targetUri.startsWith("file://")) {
    targetUri = `file://${targetUri}`;
  }

  // 3. Native Expo FileSystem (file:// or local path on non-web platforms)
  if (Platform.OS !== "web" && typeof File !== "undefined") {
    try {
      const fileRef = new File(targetUri);
      if (!fileRef.exists && targetUri.startsWith("file://")) {
        throw new Error(`File not found on device: ${targetUri}`);
      }
      if (fileRef.exists && typeof fileRef.size === "number" && fileRef.size > MAX_SAFE_BASE64_FILE_SIZE_BYTES) {
        const sizeMb = (fileRef.size / (1024 * 1024)).toFixed(1);
        throw new Error(
          `File is too large for in-memory processing (${sizeMb} MB). Files larger than 30 MB exceed device memory limits.`
        );
      }
      // Attempt direct next-gen File.base64()
      const base64 = await fileRef.base64();
      if (base64) {
        return base64;
      }
    } catch (err: any) {
      if (err?.message?.includes("File is too large") || err?.message?.includes("File not found")) {
        throw err;
      }
      const msg = err?.message || String(err);
      if (msg.includes("OutOfMemoryError") || msg.includes("allocation")) {
        throw new Error(`Device ran out of memory reading file (${targetUri}). The file is too large for memory processing.`);
      }
      console.warn(`[FileReader] Direct File.base64() failed for URI "${targetUri}":`, msg);
    }
  }

  // 4. Fallback for content:// URIs on Android: copy to a local cache file first
  if (targetUri.startsWith("content://") && typeof File !== "undefined" && typeof Paths !== "undefined" && Paths?.cache) {
    try {
      const tempFile = new File(Paths.cache, `read_${Date.now()}_temp`);
      const srcFile = new File(targetUri);
      srcFile.copy(tempFile);

      // Check size of cached temp copy before loading into memory
      if (tempFile.exists && typeof tempFile.size === "number" && tempFile.size > MAX_SAFE_BASE64_FILE_SIZE_BYTES) {
        tempFile.delete();
        const sizeMb = (tempFile.size / (1024 * 1024)).toFixed(1);
        throw new Error(
          `File is too large for in-memory processing (${sizeMb} MB). Files larger than 30 MB exceed device memory limits.`
        );
      }

      const base64 = await tempFile.base64();
      tempFile.delete();
      if (base64) {
        return base64;
      }
    } catch (copyErr: any) {
      if (copyErr?.message?.includes("File is too large")) {
        throw copyErr;
      }
      console.warn(`[FileReader] Content URI copy fallback failed for "${targetUri}":`, copyErr?.message || copyErr);
    }
  }

  // 5. Generic fetch fallback (if targetUri is any other fetchable URI, e.g. blob: on non-web or network stream)
  if (typeof fetch === "function" && (targetUri.startsWith("blob:") || targetUri.startsWith("http://") || targetUri.startsWith("https://"))) {
    try {
      const response = await fetch(targetUri);
      if (response.ok) {
        const blob = await response.blob();
        return await blobToBase64(blob);
      }
    } catch (fallbackErr: any) {
      console.warn(`[FileReader] Universal fetch fallback failed for "${targetUri}":`, fallbackErr?.message || fallbackErr);
    }
  }

  throw new Error(`Unable to read file at ${targetUri}`);
}

